<template>
    <section class="dp-layout class-affair-summary">
        <section class="dp-left">
            <DepartmentPane directory-scope="teaching" pane-title="组织架构" default-select-root @tree-node-click="treeNodeClick"></DepartmentPane>
        </section>
        <section class="dp-right" v-if="schoolId > 0">
            <div class="scope-hint" v-if="scopeLabel">当前：{{ scopeLabel }}</div>
            <el-form :inline="true" size="small" class="toolbar">
                <el-form-item label="时段">
                    <el-select v-model="windowId" placeholder="请选择" style="width:280px" @change="load(1)">
                        <el-option v-for="w in windows" :key="w.Id" :label="windowLabel(w)" :value="w.Id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="状态">
                    <el-select v-model="completionFilter" clearable placeholder="全部" style="width:120px" @change="load(1)">
                        <el-option label="未交" value="missing" />
                        <el-option label="已交" value="submitted" />
                        <el-option label="未启用" value="disabled" />
                    </el-select>
                </el-form-item>
                <el-form-item>
                    <el-button
                        v-if="hasActionPermission('handleExport')"
                        type="success"
                        size="small"
                        icon="el-icon-download"
                        :loading="exporting"
                        :disabled="!windowId"
                        @click="handleExport"
                    >导出</el-button>
                </el-form-item>
            </el-form>
            <div class="stat-row" v-if="summary">
                <button type="button" class="stat-chip is-missing" :class="{ active: completionFilter === 'missing' }" @click="setFilter('missing')">
                    <span class="stat-chip__label">未交</span>
                    <span class="stat-chip__value">{{ summary.MissingCount || 0 }}</span>
                </button>
                <button type="button" class="stat-chip is-submitted" :class="{ active: completionFilter === 'submitted' }" @click="setFilter('submitted')">
                    <span class="stat-chip__label">已交</span>
                    <span class="stat-chip__value">{{ summary.SubmittedCount || 0 }}</span>
                </button>
                <button type="button" class="stat-chip is-disabled" :class="{ active: completionFilter === 'disabled' }" @click="setFilter('disabled')">
                    <span class="stat-chip__label">未启用</span>
                    <span class="stat-chip__value">{{ summary.DisabledCount || 0 }}</span>
                </button>
                <button type="button" class="stat-chip is-all" :class="{ active: !completionFilter }" @click="setFilter('')">
                    <span class="stat-chip__label">全部</span>
                </button>
            </div>
            <el-table :data="rows" v-loading="loading" border size="small" class="summary-table">
                <el-table-column prop="CampusName" label="校区" min-width="120" show-overflow-tooltip />
                <el-table-column prop="ClassName" label="班级" min-width="120" show-overflow-tooltip />
                <el-table-column label="状态" min-width="110" align="center">
                    <template slot-scope="scope">
                        <el-tag :type="statusTagType(scope.row.CompletionStatus)" size="mini" effect="plain">
                            {{ scope.row.CompletionStatusText || '-' }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="提交人" min-width="110" show-overflow-tooltip>
                    <template slot-scope="scope">
                        <span :class="{ muted: !scope.row.TeacherName }">{{ scope.row.TeacherName || '-' }}</span>
                    </template>
                </el-table-column>
                <el-table-column label="提交时间" min-width="160" show-overflow-tooltip>
                    <template slot-scope="scope">
                        <span :class="{ muted: !scope.row.SubmitTime }">{{ scope.row.SubmitTime || '-' }}</span>
                    </template>
                </el-table-column>
                <el-table-column label="班级分" min-width="100" align="center">
                    <template slot-scope="scope">
                        <span v-if="scope.row.Score != null && scope.row.CompletionStatus === 'submitted'" class="score-text">+{{ formatScore(scope.row.Score) }}</span>
                        <span v-else class="muted">-</span>
                    </template>
                </el-table-column>
            </el-table>
            <el-pagination class="pager" background layout="total, prev, pager, next" :current-page="page" :page-size="pageSize" :total="total" @current-change="load" />
        </section>
        <section class="dp-right" v-else><div class="empty-tip">请在左侧选择部门</div></section>
    </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import { getButtonList } from '../../promissionRouter';
import { getClassAffairWindows, getClassAffairCompletionSummary, exportClassAffairCompletionSummary } from '../../api/modules/classAffair';

export default {
    name: 'ClassAffairSummary',
    components: { DepartmentPane },
    data() {
        return {
            schoolId: 0,
            departmentId: 0,
            scopeLabel: '',
            buttonList: [],
            windows: [],
            windowId: null,
            completionFilter: 'missing',
            summary: null,
            rows: [],
            loading: false,
            exporting: false,
            page: 1,
            pageSize: 20,
            total: 0
        };
    },
    created() {
        this.buttonList = getButtonList(this.$route.path) || [];
    },
    methods: {
        hasActionPermission(func) {
            if (!this.buttonList || !this.buttonList.length) return true;
            return this.buttonList.some(button => button.Func === func);
        },
        findAncestorDeptId(node, orgType) {
            let cur = node;
            const target = String(orgType || '').toLowerCase();
            while (cur) {
                const row = cur.data || cur;
                const type = String((row.DeptOrgType || row.deptOrgType) || '').toLowerCase();
                if (type === target) {
                    const id = row.value != null ? row.value : row.Id;
                    return id && String(id) !== '0' ? id : 0;
                }
                cur = cur.parent;
            }
            return 0;
        },
        async treeNodeClick(data, node) {
            if (!data) return;
            const orgType = String(data.DeptOrgType || data.deptOrgType || '').toLowerCase();
            const deptId = data.value != null ? data.value : data.Id;
            if (!deptId || String(deptId) === '0') return;
            const schoolId = orgType === 'school' || orgType === 'affiliateschool'
                ? deptId
                : (this.findAncestorDeptId(node, 'school')
                    || this.findAncestorDeptId(node, 'affiliateschool')
                    || this.schoolId);
            if (!schoolId) return;
            const schoolChanged = String(this.schoolId) !== String(schoolId);
            this.schoolId = schoolId;
            this.departmentId = deptId;
            this.scopeLabel = data.label || data.Name || '';
            if (schoolChanged || !this.windows.length) {
                this.windowId = null;
                await this.loadWindows();
                return;
            }
            this.load(1);
        },
        windowLabel(w) {
            return (w.Title || w.ItemTypeName || '') + ' ' + w.StartDate + '~' + w.EndDate;
        },
        statusTagType(status) {
            if (status === 'submitted') return 'success';
            if (status === 'missing') return 'danger';
            if (status === 'disabled') return 'info';
            return '';
        },
        formatScore(val) {
            if (val === null || val === undefined || val === '') return '-';
            const n = Number(val);
            if (isNaN(n)) return val;
            return Number.isInteger(n) ? String(n) : n.toFixed(2);
        },
        setFilter(value) {
            this.completionFilter = value;
            this.load(1);
        },
        async loadWindows() {
            const res = await getClassAffairWindows({ schoolId: this.schoolId });
            this.windows = res.data && res.data.success ? (res.data.response || []) : [];
            if (this.windows.length) {
                this.windowId = this.windows[0].Id;
                this.load(1);
            }
        },
        async load(page) {
            if (!this.windowId) return;
            this.page = page || 1;
            this.loading = true;
            try {
                const res = await getClassAffairCompletionSummary({
                    schoolId: this.schoolId,
                    departmentId: this.departmentId || 0,
                    windowId: this.windowId,
                    completionFilter: this.completionFilter || '',
                    page: this.page,
                    intPageSize: this.pageSize
                });
                if (res.data && res.data.success) {
                    this.summary = res.data.response || {};
                    this.rows = this.summary.Items || [];
                    this.total = this.summary.dataCount || 0;
                } else {
                    this.rows = [];
                    this.$message.error((res.data && res.data.msg) || '加载失败');
                }
            } finally {
                this.loading = false;
            }
        },
        async handleExport() {
            if (!this.schoolId || !this.windowId) {
                this.$message.warning('请先选择时段');
                return;
            }
            this.exporting = true;
            try {
                const res = await exportClassAffairCompletionSummary({
                    schoolId: this.schoolId,
                    departmentId: this.departmentId || 0,
                    windowId: this.windowId,
                    completionFilter: this.completionFilter || ''
                });
                const blob = res.data;
                if (blob && blob.type && blob.type.indexOf('application/json') >= 0) {
                    const text = await blob.text();
                    let msg = '导出失败';
                    try {
                        const json = JSON.parse(text);
                        msg = json.msg || msg;
                    } catch (err) { /* ignore */ }
                    this.$message.error(msg);
                    return;
                }
                if (!blob || blob.size === 0) {
                    this.$message.warning('没有数据可导出');
                    return;
                }
                const url = window.URL.createObjectURL(new Blob([blob], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }));
                const link = document.createElement('a');
                link.href = url;
                link.download = `班务完成汇总_${new Date().toISOString().slice(0, 10)}.xlsx`;
                link.click();
                window.URL.revokeObjectURL(url);
                this.$message.success('导出成功');
            } catch (e) {
                this.$message.error('导出失败');
            } finally {
                this.exporting = false;
            }
        }
    }
};
</script>

<style scoped>
.scope-hint { margin-bottom: 8px; color: #606266; font-size: 13px; }
.toolbar { margin-bottom: 8px; }
.stat-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
}
.stat-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #e8edf3;
    background: #fff;
    border-radius: 8px;
    padding: 6px 12px;
    cursor: pointer;
    transition: border-color .2s, background .2s, box-shadow .2s;
    color: #303133;
}
.stat-chip:hover {
    border-color: #c0c4cc;
}
.stat-chip.active {
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.12);
}
.stat-chip__label { font-size: 12px; color: #909399; }
.stat-chip__value { font-size: 16px; font-weight: 600; line-height: 1; }
.stat-chip.is-missing.active,
.stat-chip.is-missing:hover { border-color: #f56c6c; background: #fef0f0; }
.stat-chip.is-missing .stat-chip__value { color: #f56c6c; }
.stat-chip.is-submitted.active,
.stat-chip.is-submitted:hover { border-color: #67c23a; background: #f0f9eb; }
.stat-chip.is-submitted .stat-chip__value { color: #67c23a; }
.stat-chip.is-disabled.active,
.stat-chip.is-disabled:hover { border-color: #909399; background: #f4f4f5; }
.stat-chip.is-disabled .stat-chip__value { color: #606266; }
.stat-chip.is-all.active { border-color: #409eff; background: #ecf5ff; }
.summary-table >>> .el-table__header,
.summary-table >>> .el-table__body { width: 100% !important; }
.score-text { color: #67c23a; font-weight: 600; }
.muted { color: #c0c4cc; }
.pager { margin-top: 12px; text-align: right; }
.empty-tip { padding: 80px 0; text-align: center; color: #909399; }
</style>
